import {
  formatNumberDecimal,
  formatWithCommas,
  replaceCurrencyString,
  roundToNumericPrecision,
} from '../utils/libs';
import { formatDisplayDateTime } from '../utils/formatDisplayDateTime';
import { getOrderLineProductDisplayName } from '../utils/productDisplayName';
import type {
  DynamicTableParams,
  OrderProductItem,
  Payment_Information,
} from '../screens/checkout/components/Receipt';
import { buildReceiptTotalsSummary } from './receiptTotals';

function calculatePartialTotals(order: OrderProductItem[]): number {
  return order.reduce((total, item) => {
    const { bp_product_information } = item;
    if (bp_product_information.bp_product_price_type === 'unit') {
      const itemTotal =
        bp_product_information.bp_product_selling_price *
        ((item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0));
      return total + itemTotal;
    }
    const currentQuantity =
      item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0);
    return (
      total + bp_product_information.bp_product_selling_price * currentQuantity
    );
  }, 0);
}

const getOrderLineDisplayName = (item: OrderProductItem): string =>
  getOrderLineProductDisplayName(item);

export function generateReceiptHTML({
  receipt,
  business,
  employeedata,
  currency,
  totalsMode,
}: DynamicTableParams): string {
  const currencyText = replaceCurrencyString('Ksh', currency);
  const date = formatDisplayDateTime(new Date());

  const name = (business?.name || '').trim();
  const desc = (business?.description || '').trim();
  const phone = employeedata?.bp_owner_phone_number || 'N/A';
  const employeeName = employeedata?.bp_employee_name || '';
  const receiptId = String(
    receipt?.ReceiptID ||
      (receipt as any)?.receiptID ||
      (receipt as any)?.bp_order_master_receipt_reference_id ||
      '',
  ).trim();

  const order = receipt?.order || [];
  const pi: Payment_Information = receipt.Payment_Information;
  const totalAmount = calculatePartialTotals(order);

  const itemRows = order
    .map((item, idx) => {
      if (typeof item !== 'object' || !item.bp_product_information) return '';
      const unitSuffix =
        item.bp_product_information.bp_product_price_type === 'unit'
          ? item.bp_product_information.bp_product_unit_of_mass || ''
          : '';

      const itemDesc = getOrderLineDisplayName(item);

      const currQuantity = roundToNumericPrecision(
        item.bp_product_information.bp_product_price_type === 'unit'
          ? (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0)
          : item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0),
      );

      const price = item.bp_product_information.bp_product_selling_price;
      const amount = roundToNumericPrecision(price * currQuantity);

      return `<tr>
        <td>${idx + 1}</td>
        <td>${itemDesc}</td>
        <td style="text-align:center">${formatNumberDecimal(currQuantity)}${unitSuffix}</td>
        <td style="text-align:right">${formatWithCommas(price)}</td>
        <td style="text-align:right">${formatWithCommas(amount)}</td>
      </tr>`;
    })
    .filter(Boolean)
    .join('');

  const summary = buildReceiptTotalsSummary(pi, totalAmount, totalsMode ?? 'print');
  const totalLineVal = formatWithCommas(summary.grandTotal);

  let totalsHTML = '';
  for (const r of summary.rows) {
    const cellStyle = ` style="font-weight:${
      r.strong ? 600 : 400
    }${r.emphasis === 'balance' ? ';color:#b91c1c' : ''}"`;
    const valueStyle = `text-align:right${r.strong ? ';font-weight:600' : ''}${
      r.emphasis === 'balance' ? ';color:#b91c1c' : ''
    }`;
    const formatted =
      r.emphasis === 'change'
        ? formatWithCommas(r.amount, 0, true)
        : formatWithCommas(r.amount);
    totalsHTML += `<tr><td${cellStyle}>${r.label}</td><td style="${valueStyle}">${currencyText} ${formatted}</td></tr>`;
  }
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #222; font-size: 13px; }
    .receipt { max-width: 380px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 16px; border-bottom: 1px dashed #999; padding-bottom: 12px; }
    .header h1 { font-size: 18px; margin-bottom: 4px; }
    .header p { font-size: 12px; color: #555; margin: 2px 0; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .items-table th { border-bottom: 1px solid #333; padding: 6px 4px; font-size: 11px; text-align: left; }
    .items-table td { padding: 5px 4px; font-size: 12px; border-bottom: 1px solid #eee; }
    .totals-table { width: 100%; margin-top: 8px; border-top: 1px dashed #999; padding-top: 8px; }
    .totals-table td { padding: 4px 0; font-size: 12px; }
    .total-line { font-size: 16px; font-weight: bold; text-align: center; margin: 16px 0; padding: 8px; background: #f5f5f5; border-radius: 4px; }
    .paid-stamp { text-align: center; margin: 8px 0 12px; font-size: 14px; font-weight: bold; color: #166534; letter-spacing: 2px; border: 2px solid #166534; padding: 6px; border-radius: 4px; }
    .footer { text-align: center; margin-top: 16px; border-top: 1px dashed #999; padding-top: 12px; font-size: 11px; color: #555; }
    .footer p { margin: 3px 0; }
    .footer .brand { font-weight: bold; margin-top: 8px; font-size: 12px; color: #333; }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h1>${name}</h1>
      ${desc ? `<p>${desc}</p>` : ''}
      <p>Tel: ${phone}</p>
      ${receiptId ? `<p>Receipt: ${receiptId}</p>` : ''}
      <p>${date}</p>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Item</th>
          <th style="text-align:center">Qty</th>
          <th style="text-align:right">Price</th>
          <th style="text-align:right">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
    </table>

    <table class="totals-table">
      ${totalsHTML}
    </table>

    <div class="total-line">
      Sub-Total: ${currencyText} ${totalLineVal}
    </div>
    ${
      summary.isPartialLedger && summary.isPaidInFull
        ? '<div class="paid-stamp">PAID IN FULL</div>'
        : ''
    }

    <div class="footer">
      <p>Served by: ${employeeName}</p>
      <p>GOODS ONCE SOLD WILL NOT BE RE-ACCEPTED</p>
      <p>Thank you for choosing ${name}!</p>
      <p class="brand">BizPlus POS</p>
    </div>
  </div>
</body>
</html>`;
}
